/**
 * Comparability blocks for the pool an event is actually holding.
 *
 * The structural guard (poolViolations) was enforced in exactly one place:
 * createSession, against the Track's pool. Nothing re-checked after that, and
 * S2 is where the pool is really assembled — so a set refused at creation
 * sailed through when the same items were added one step later, and S5 ranked
 * them. "We ranked a strategy against a button" is the pattern this rule exists
 * to prevent, and it was preventable only by the door nobody used.
 *
 * Same detector, same catalogue, evaluated against event.items so it follows the
 * pool wherever it is built.
 *
 * Two exports, one computation. `useComparabilityBlocks` is the enforcement
 * view — blocks only, what stops a set from being ranked. `useComparabilityFindings`
 * is the whole report, which the structural moment (R2) needs because a warn is
 * still something a room should reckon with before it scores anything. They must
 * not drift, so the Track declared-range refinement below lives in one place and
 * both read it.
 */
import { useMemo } from 'react';
import { computeViolations, type PoolViolation } from '../../lib/poolViolations';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import { useTrackStore } from '../../stores/prioritization/trackStore';
import { getTrackCellsFromMetadata } from '../../types/prioritization/track';
import type { SessionEvent } from './types';

/**
 * Every violation over the event's pool, blocks and warns alike, with the
 * Track's declared Comparison Range honoured.
 */
export function useComparabilityFindings(event: SessionEvent | null): PoolViolation[] {
  const allItems = useItemsStore((s) => s.items);
  const tracks = useTrackStore((s) => s.tracks);
  const edges = useItemRelationshipsStore((s) => s.edges);
  const groups = useItemRelationshipsStore((s) => s.groups);
  const memberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const lineageEvents = useItemRelationshipsStore((s) => s.lineageEvents);

  // Platform items only: a typed-in option has no category or altitude to
  // compare, and flagging it would be noise rather than a finding.
  const memberIds = useMemo(
    () => (event?.items ?? [])
      .filter((i) => i.source === 'platform' && !i.id.startsWith('item_'))
      .map((i) => i.id),
    [event?.items],
  );

  // A Track may declare a Comparison RANGE — several cells it treats as
  // legitimately comparable — not just a single Comparison Cell (canon 01: "a
  // Comparison Cell (Item Category × Altitude) — or a configured Comparison
  // Range"). B1 only knows how to count cells, so on a Track with a declared
  // range it reports a mismatch for exactly the spread that Track was
  // configured to allow. Honour the declaration: inside a declared range this
  // is not a finding.
  const declaredCells = useMemo(() => {
    if (!event?.trackId) return null;
    const track = tracks.find((t) => t.id === event.trackId);
    return getTrackCellsFromMetadata(track?.metadata as Record<string, unknown> | null | undefined);
  }, [tracks, event?.trackId]);

  return useMemo(() => {
    if (memberIds.length < 2) return [];
    const report = computeViolations(memberIds, {
      items: allItems,
      edges: edges ?? [],
      groups: groups ?? [],
      memberships: memberships ?? [],
      lineageEvents: lineageEvents ?? [],
    });
    return report.violations.filter((v) => {
      // Every offender inside the Track's declared range → the spread is
      // sanctioned, not a mismatch. Other patterns (unresolved duplicate
      // cluster, half a package) are unaffected — a range says nothing about them.
      if (v.patternId === 'B1_altitude_salad' && declaredCells && declaredCells.length > 1) {
        const inRange = (id: string) => {
          const it = allItems.find((i) => i.id === id) as
            | { itemCategory?: string | null; altitudeLevel?: number | null }
            | undefined;
          return declaredCells.some(
            (c) => c.itemCategory === it?.itemCategory && c.altitudeLevel === it?.altitudeLevel,
          );
        };
        if (v.itemIds.every(inRange)) return false;
      }
      return true;
    });
  }, [memberIds, allItems, edges, groups, memberships, lineageEvents, declaredCells]);
}

/**
 * Blocking violations only — the enforcement view every existing caller uses.
 *
 * Derived from the full report rather than computing its own, so a change to
 * the declared-range refinement cannot make the enforcement view and the
 * structural moment disagree about the same pool.
 */
export function useComparabilityBlocks(event: SessionEvent | null): PoolViolation[] {
  const findings = useComparabilityFindings(event);
  return useMemo(() => findings.filter((v) => v.disposition === 'block'), [findings]);
}
